import { useHandler } from "@redotech/react-util/hook";
import { type Translate, useTranslation } from "@redotech/redo-i18n";
import {
  FedexClaimInfo,
  FedexDamageInfo,
  FedexDamageToContents,
  FedexDamageToOuter,
  FedexPackagingMaterial,
  isFedexDamageInfoComplete,
} from "@redotech/redo-package-protection-common/shipping-claims/carrier-claim-info/fedex";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";

function enumOptions<T extends string>(
  values: T[],
  label: (value: T) => string,
): RedoListItem<T>[] {
  return values.map((v) => ({ type: "text", text: label(v), value: v, id: v }));
}

function getDamageToContentsLabel(damage: FedexDamageToContents, t: Translate) {
  switch (damage) {
    case FedexDamageToContents.NONE:
      return t("None", { context: "damage to contents" });
    case FedexDamageToContents.BENT:
      return t("Bent");
    case FedexDamageToContents.BROKEN:
      return t("Contents broken");
    case FedexDamageToContents.MISSING:
      return t("Contents missing");
    case FedexDamageToContents.PROTRUDING:
      return t("Contents protruding");
  }
}

function getDamageToOuterLabel(damage: FedexDamageToOuter, t: Translate) {
  switch (damage) {
    case FedexDamageToOuter.CLOSURES_OPEN:
      return t("Closures open");
    case FedexDamageToOuter.CORNER_CRUSHED:
      return t("Corner of box crushed");
    case FedexDamageToOuter.CRUSHED:
      return t("Crushed");
    case FedexDamageToOuter.NO_VISIBLE_DAMAGE:
      return t("No visible damage");
    case FedexDamageToOuter.PUNCTURED:
      return t("Punctured");
    case FedexDamageToOuter.RIPPED:
      return t("Ripped/torn");
    case FedexDamageToOuter.RETAPED:
      return t("Retaped");
    case FedexDamageToOuter.WET:
      return t("Wet");
    case FedexDamageToOuter.OTHER:
      return t("Other, please specify", { context: "damage to packaging" });
  }
}

function getPackagingMaterialLabel(
  material: FedexPackagingMaterial,
  t: Translate,
) {
  switch (material) {
    case FedexPackagingMaterial.NONE:
      return t("None", { context: "packaging material" });
    case FedexPackagingMaterial.BUBBLE_WRAP:
      return t("Bubble wrap");
    case FedexPackagingMaterial.CORNER_POST:
      return t("Corner post");
    case FedexPackagingMaterial.CORRUGATED_LINERS:
      return t("Corrugated liners");
    case FedexPackagingMaterial.MOLDED_FOAM:
      return t("Molded foam");
    case FedexPackagingMaterial.PLASTIC_BAGS:
      return t("Plastic bags");
    case FedexPackagingMaterial.PAPER:
      return t("Paper");
    case FedexPackagingMaterial.SHREDDED_PAPER:
      return t("Shredded paper");
    case FedexPackagingMaterial.STYROFOAM_PEANUTS:
      return t("Styrofoam peanuts");
    case FedexPackagingMaterial.OTHER:
      return t("Other, please specify", { context: "packaging material" });
  }
}

export type PartialFedexDamageInfo = Partial<FedexDamageInfo>;

export function buildFedexClaimInfo(
  d: PartialFedexDamageInfo,
): FedexClaimInfo | null {
  if (!isFedexDamageInfoComplete(d)) {
    return null;
  }
  return { damage: d };
}

interface FedexClaimInfoSectionProps {
  value: PartialFedexDamageInfo;
  setValue: (next: PartialFedexDamageInfo) => void;
}

export const FedexClaimInfoSection = memo(function FedexClaimInfoSection({
  value,
  setValue,
}: FedexClaimInfoSectionProps) {
  const { t } = useTranslation();
  const update = useHandler((patch: Partial<FedexDamageInfo>) => {
    setValue({ ...value, ...patch });
  });

  const {
    damageToContentsOptions,
    damageToOuterOptions,
    packagingMaterialOptions,
  } = useFedexDamageSelectOptions();

  return (
    <Flex dir="column" gap="md">
      <Text fontSize="sm" fontWeight="semibold">
        {t("FedEx damage details")}
      </Text>

      <RedoSingleSelectDropdownInput
        label={t("Type of damage to contents")}
        options={damageToContentsOptions}
        optionSelected={(item) => update({ damageToContents: item.item.value })}
        selectedItem={damageToContentsOptions.find(
          (o) => o.value === value.damageToContents,
        )}
      />

      <RedoSingleSelectDropdownInput
        label={t("Type of damage to outer packaging")}
        options={damageToOuterOptions}
        optionSelected={(item) => update({ damageToOuter: item.item.value })}
        selectedItem={damageToOuterOptions.find(
          (o) => o.value === value.damageToOuter,
        )}
      />

      {value.damageToOuter === FedexDamageToOuter.OTHER && (
        <RedoTextInput
          label={t("Describe the damage to outer packaging")}
          maxLength={66}
          setValue={(s) => update({ damageToOuterOther: s })}
          value={value.damageToOuterOther ?? ""}
        />
      )}

      <RedoSingleSelectDropdownInput
        label={t("Packaging materials")}
        options={packagingMaterialOptions}
        optionSelected={(item) =>
          update({ packagingMaterials: item.item.value })
        }
        selectedItem={packagingMaterialOptions.find(
          (o) => o.value === value.packagingMaterials,
        )}
      />

      {value.packagingMaterials === FedexPackagingMaterial.OTHER && (
        <RedoTextInput
          label={t("Describe the packaging materials")}
          maxLength={66}
          setValue={(s) => update({ packagingMaterialsOther: s })}
          value={value.packagingMaterialsOther ?? ""}
        />
      )}
    </Flex>
  );
});

function useFedexDamageSelectOptions() {
  const { t } = useTranslation();
  const damageToContentsOptions = useMemo(
    () =>
      enumOptions(Object.values(FedexDamageToContents), (value) =>
        getDamageToContentsLabel(value, t),
      ),
    [t],
  );
  const damageToOuterOptions = useMemo(
    () =>
      enumOptions(Object.values(FedexDamageToOuter), (value) =>
        getDamageToOuterLabel(value, t),
      ),
    [t],
  );
  const packagingMaterialOptions = useMemo(
    () =>
      enumOptions(Object.values(FedexPackagingMaterial), (value) =>
        getPackagingMaterialLabel(value, t),
      ),
    [t],
  );
  return {
    damageToContentsOptions,
    damageToOuterOptions,
    packagingMaterialOptions,
  };
}
